import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { catchError, map, shareReplay, throwError } from 'rxjs';

import {
  DianError,
  Currency,
  XMLResponse,
  InvoiceRequest,
  DianResponse,
  CreditNoteRequest,
  DebitNoteRequest,
  NoteConcept,
  SupportDocumentAdjustmentRequest,
  SupportDocumentRequest,
} from '@shared-models';

import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class DianService {
  private http = inject(HttpClient);
  apiUrl = environment.api_dian;
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  handleError(error: DianError) {
    console.log(
      `Backend returned code ${error.status_code}, body was: `,
      error.error,
    );
  }

  getStatus(idNumber: string, company: string) {
    return this.http
      .get<
        DianResponse<XMLResponse>
      >(this.apiUrl + `getStatus/` + `${idNumber}/` + `${company}`, this.httpOptions)
      .pipe(
        map((result) => {
          return result.data;
        }),
        shareReplay(1),
        catchError((error) => {
          this.handleError(error);
          return throwError(error);
        }),
      );
  }

  getStatusZip(idNumber: string, company: string) {
    return this.http
      .get(
        this.apiUrl + `getStatusZip/` + `${idNumber}/` + `${company}`,
        this.httpOptions,
      )
      .pipe(
        map((result) => {
          return result;
        }),
        shareReplay(1),
        catchError((error) => {
          this.handleError(error);
          return throwError(error);
        }),
      );
  }

  sendBillSyncInvoice(invoice: InvoiceRequest) {
    return this.http
      .post<
        DianResponse<XMLResponse>
      >(this.apiUrl + `sendBillSync`, invoice, this.httpOptions)
      .pipe(
        map((result) => result.data),
        shareReplay(1),
        catchError((error) => {
          this.handleError(error);
          return throwError(error);
        }),
      );
  }

  sendBillSyncNote(note: CreditNoteRequest | DebitNoteRequest) {
    return this.http
      .post<
        DianResponse<XMLResponse>
      >(this.apiUrl + `sendBillSync`, note, this.httpOptions)
      .pipe(
        map((result) => result.data),
        shareReplay(1),
        catchError((error) => {
          this.handleError(error);
          return throwError(error);
        }),
      );
  }

  sendBillSyncSupportDocument(purchase: SupportDocumentRequest) {
    return this.http
      .post<
        DianResponse<XMLResponse>
      >(this.apiUrl + `sendBillSync`, purchase, this.httpOptions)
      .pipe(
        map((result) => result.data),
        shareReplay(1),
        catchError((error) => {
          this.handleError(error);
          return throwError(error);
        }),
      );
  }

  sendBillSyncAdjustmentNote(adjustment: SupportDocumentAdjustmentRequest) {
    return this.http
      .post<
        DianResponse<XMLResponse>
      >(this.apiUrl + `sendBillSync`, adjustment, this.httpOptions)
      .pipe(
        map((result) => result.data),
        shareReplay(1),
        catchError((error) => {
          this.handleError(error);
          return throwError(error);
        }),
      );
  }

  getNumberingRange(
    accountCode: string,
    accountCodeT: string,
    softwareCode: string,
    company: string,
  ) {
    return this.http
      .get(
        this.apiUrl +
          `getNumberingRange/` +
          `${accountCode}/` +
          `${accountCodeT}/` +
          `${softwareCode}/` +
          `${company}`,
        this.httpOptions,
      )
      .pipe(
        map((result) => {
          return result;
        }),
        shareReplay(1),
        catchError((error) => {
          this.handleError(error);
          return throwError(error);
        }),
      );
  }

  getCurrencies() {
    return this.http
      .get<
        DianResponse<Currency[]>
      >(this.apiUrl + `getCurrencies`, this.httpOptions)
      .pipe(
        map((result) => result.data),
        shareReplay(1),
        catchError((error) => {
          this.handleError(error);
          return throwError(error);
        }),
      );
  }

  getDianCities() {
    return this.http.get(this.apiUrl + `getDianCities`, this.httpOptions).pipe(
      map((result) => {
        return result;
      }),
      shareReplay(1),
      catchError((error) => {
        this.handleError(error);
        return throwError(error);
      }),
    );
  }

  getDianNoteConcepts(idNumber: number) {
    return this.http
      .get<
        DianResponse<NoteConcept[]>
      >(this.apiUrl + `getDianNoteConcepts/` + idNumber, this.httpOptions)
      .pipe(
        map((result) => {
          return result;
        }),
        shareReplay(1),
        catchError((error) => {
          this.handleError(error);
          return throwError(error);
        }),
      );
  }
}
